import type { GeoLevel, TargetingConfig } from '../types/metaAdDraft.js';

// Stage 7A D1-followup — geo-level migration. The geographic layers (countries /
// regions / cities) are now MUTUALLY EXCLUSIVE, selected by a single
// TargetingConfig.geoLevel ('country' | 'region' | 'city'). Drafts created before
// this field existed carry geoLevel: null/undefined; this pure helper derives it
// from whichever single layer is populated. Applied at the same boundaries as
// migrateCreativeShape: server read path, zod request preprocess, client
// draftToInput. Idempotent — a draft that already has a valid geoLevel keeps it.

const VALID: ReadonlySet<string> = new Set<GeoLevel>(['country', 'region', 'city']);

function len(v: unknown): number {
  return Array.isArray(v) ? v.length : 0;
}

// Derive the geo level for a (possibly legacy / loosely-typed) targeting object:
//   - an already-valid geoLevel wins (idempotent)
//   - exactly one populated layer → that layer's level
//   - zero or MULTIPLE populated layers → 'country' (safest default; an edge case
//     for legacy multi-layer drafts the AM can re-pick in the UI)
export function deriveGeoLevel(t: {
  geoLevel?: unknown;
  countries?: unknown;
  regions?: unknown;
  cities?: unknown;
}): GeoLevel {
  if (typeof t?.geoLevel === 'string' && VALID.has(t.geoLevel)) return t.geoLevel as GeoLevel;
  const hasCountry = len(t?.countries) > 0;
  const hasRegion = len(t?.regions) > 0;
  const hasCity = len(t?.cities) > 0;
  const populated = [hasCountry, hasRegion, hasCity].filter(Boolean).length;
  if (populated === 1) {
    if (hasRegion) return 'region';
    if (hasCity) return 'city';
    return 'country';
  }
  return 'country';
}

// ---------- parent-country derivation for city targeting ----------
//
// Meta REQUIRES a country alongside city targeting. The city search results carry
// a `path` breadcrumb whose COUNTRY is the LAST element (Zernio shape: [region, …,
// country], e.g. ['Queensland','Australia'] — ancestry, not the place's own name);
// we resolve that label → ISO-3166-1 alpha-2 so the targeting spec / publish body
// always carry the parent country. Without it the reach estimate comes back empty
// (the UI "never loads") and publish is blocked.
//
// Coverage is the franchise markets (matches the Countries picker list) + the
// common spoken aliases (USA / UK / …). An unknown label resolves to null so a
// city with no resolvable country is FLAGGED at validation rather than silently
// dropped.
const COUNTRY_NAME_TO_CODE: Readonly<Record<string, string>> = {
  australia: 'AU',
  'new zealand': 'NZ',
  'united states': 'US',
  'united states of america': 'US',
  usa: 'US',
  'united kingdom': 'GB',
  uk: 'GB',
  'great britain': 'GB',
  canada: 'CA',
  ireland: 'IE',
  singapore: 'SG',
  'south africa': 'ZA',
  india: 'IN',
  philippines: 'PH',
  germany: 'DE',
  france: 'FR',
  netherlands: 'NL',
  'united arab emirates': 'AE',
};

// Resolve a country label (or an already-alpha-2 code) → ISO-3166-1 alpha-2.
// Returns null for anything unrecognised. Pure.
export function countryCodeFromName(name: unknown): string | null {
  if (typeof name !== 'string') return null;
  const n = name.trim();
  if (!n) return null;
  // Check named aliases first so spoken 2-letter forms that are NOT ISO codes
  // (e.g. 'UK' → GB) resolve correctly before the alpha-2 passthrough.
  const alias = COUNTRY_NAME_TO_CODE[n.toLowerCase()];
  if (alias) return alias;
  if (/^[A-Za-z]{2}$/.test(n)) return n.toUpperCase(); // already an alpha-2 code
  return null;
}

// The parent country of a single city selection: prefer an explicitly-stored
// countryCode, else derive from the search-result path breadcrumb. The country is the
// LAST element of the Zernio breadcrumb ([region, …, country]); we scan from the END
// (the country position) for the first label that resolves, so it works whichever
// position the country sits in (tolerant of the old [country, …] shape too). Returns
// null when nothing resolves. Pure + tolerant of loose input.
export function cityCountryCode(city: {
  countryCode?: string | null;
  path?: string[] | null;
}): string | null {
  const fromCode = countryCodeFromName(city?.countryCode);
  if (fromCode) return fromCode;
  if (Array.isArray(city?.path)) {
    for (let i = city.path.length - 1; i >= 0; i--) {
      const code = countryCodeFromName(city.path[i]);
      if (code) return code;
    }
  }
  return null;
}

// The countries to SEND to Meta — for BOTH the reach estimate and publish (they
// must NOT diverge). Country and city/region targeting are MUTUALLY EXCLUSIVE at
// Meta: a city/region key already encodes its location, so sending the parent
// country alongside it makes Meta REJECT publish ("Remove a conflicting location")
// AND silently NEVER-READY the reach estimate (Miki confirmed 2026-06-15 — the
// same root cause for both). So: any sub-country geo present (cities/regions) →
// [] (city-only); country-LEVEL (no cities/regions) → the countries verbatim.
//
// The account country is used to scope the city SEARCH only — it is NEVER sent to
// Meta's estimate or publish. Pure; tolerant of loose input.
export function metaCountries(t: {
  countries?: unknown;
  cities?: unknown;
  regions?: unknown;
  places?: unknown;
  neighborhoods?: unknown;
  zips?: unknown;
  customLocations?: unknown;
}): string[] {
  const hasSubCountryGeo =
    (Array.isArray(t?.cities) && t.cities.length > 0) ||
    (Array.isArray(t?.regions) && t.regions.length > 0) ||
    // places + neighborhoods + zips are sub-country geo too (geo_locations.places /
    // .neighborhoods / .zips) — they suppress a country-level target like a city does.
    (Array.isArray(t?.places) && t.places.length > 0) ||
    (Array.isArray(t?.neighborhoods) && t.neighborhoods.length > 0) ||
    (Array.isArray(t?.zips) && t.zips.length > 0) ||
    // Audience-builder Phase 1 — a custom-location pin is sub-country geo too (Meta
    // geo_locations.custom_locations), so it suppresses a country-level target the
    // same way a city does (mixing country + custom_locations conflicts/over-targets).
    (Array.isArray(t?.customLocations) && t.customLocations.length > 0);
  if (hasSubCountryGeo) return [];
  return Array.isArray(t?.countries)
    ? Array.from(new Set(t.countries.filter((c): c is string => typeof c === 'string' && c.trim() !== '')))
    : [];
}

// ---------- city radius bounds (Meta's accepted range, verified empirically) ----------
//
// Meta rejects an out-of-band city radius ("radius isn't within the specified
// bounds"). SINGLE SOURCE OF TRUTH for the per-unit band — used by the radius
// input, the unit-switch re-clamp, pre-publish validation, and the reach/publish
// radius emit. A stored radius of 0 is the "no explicit radius, use Meta's default"
// sentinel and is left alone (not clamped) by callers.
export type DistanceUnit = 'kilometer' | 'mile';

export const CITY_RADIUS_BOUNDS: Readonly<Record<DistanceUnit, { min: number; max: number }>> = {
  kilometer: { min: 17, max: 80 },
  mile: { min: 10, max: 50 },
};

// The default radius applied when a city is added (the band minimum for the unit).
export function defaultCityRadius(unit: DistanceUnit): number {
  return CITY_RADIUS_BOUNDS[unit].min;
}

// Clamp a (positive) radius into the unit's [min, max] band. Pure.
export function clampCityRadius(radius: number, unit: DistanceUnit): number {
  const { min, max } = CITY_RADIUS_BOUNDS[unit];
  if (!Number.isFinite(radius)) return min;
  return Math.min(max, Math.max(min, radius));
}

// ---------- custom-location (pin + radius) bounds — Audience-builder Phase 1 ----------
//
// Meta's geo_locations.custom_locations accept a SMALLER minimum than cities (a
// point-radius can be tight around a single venue). VENDOR-VERIFY: Meta's
// documented custom-location radius band is 1–80 km (1–50 mi) — confirm against a
// live publish before relying on the exact min (the OpenAPI only says radius > 0).
// SINGLE SOURCE OF TRUTH for the per-unit band — used by the map radius-drag clamp,
// the unit-switch re-clamp, pre-publish validation, and the reach/publish emit.
export const CUSTOM_LOCATION_RADIUS_BOUNDS: Readonly<
  Record<DistanceUnit, { min: number; max: number }>
> = {
  kilometer: { min: 1, max: 80 },
  mile: { min: 1, max: 50 },
};

// The default radius for a freshly-dropped pin — a sensible local-area default (a
// franchise catchment), comfortably inside the band so the AM can drag either way.
export function defaultCustomLocationRadius(unit: DistanceUnit): number {
  return unit === 'mile' ? 6 : 10;
}

// Clamp a (positive) custom-location radius into the unit's band. Pure.
export function clampCustomLocationRadius(radius: number, unit: DistanceUnit): number {
  const { min, max } = CUSTOM_LOCATION_RADIUS_BOUNDS[unit];
  if (!Number.isFinite(radius)) return defaultCustomLocationRadius(unit);
  return Math.min(max, Math.max(min, radius));
}

// ---------- place radius bounds (Audience-builder places) ----------
// Per the spec, a PLACE radius uses the SAME 1–80 km / 1–50 mi band as a custom
// location (NOT the city 17 km min). Aliased so the place clamp/UI reads clearly and
// can diverge later if Meta's place band ever differs.
export const PLACE_RADIUS_BOUNDS = CUSTOM_LOCATION_RADIUS_BOUNDS;
export function defaultPlaceRadius(unit: DistanceUnit): number {
  return defaultCustomLocationRadius(unit);
}
export function clampPlaceRadius(radius: number, unit: DistanceUnit): number {
  return clampCustomLocationRadius(radius, unit);
}

// Normalise a stored/loose targeting object so it always carries a concrete
// geoLevel. Tolerant of non-object input (returned as-is so a downstream zod
// schema can reject it). Idempotent.
const VALID_LOCATION_TYPES = new Set(['home', 'home_recent', 'recent']);

export function migrateTargetingShape(targeting: any): TargetingConfig {
  if (!targeting || typeof targeting !== 'object') return targeting;
  return {
    ...targeting,
    geoLevel: deriveGeoLevel(targeting),
    // D2.15 — legacy drafts predate locationTypes → default to Meta's broad default.
    locationTypes: VALID_LOCATION_TYPES.has(targeting.locationTypes)
      ? targeting.locationTypes
      : 'home_recent',
    // Audience-builder — legacy drafts predate places/neighborhoods/customLocations → [].
    // (Read paths + the zod preprocess both run this, so the fields are always concrete.)
    places: Array.isArray(targeting.places) ? targeting.places : [],
    neighborhoods: Array.isArray(targeting.neighborhoods) ? targeting.neighborhoods : [],
    zips: Array.isArray(targeting.zips) ? targeting.zips : [],
    customLocations: Array.isArray(targeting.customLocations) ? targeting.customLocations : [],
    // Audience-builder — manual exclusions. Legacy → all empty.
    excluded: normalizeExcludedGeo(targeting.excluded),
  };
}

// Normalize a (possibly missing/partial) excluded geo bucket to a complete shape.
export function normalizeExcludedGeo(e: any): TargetingConfig['excluded'] {
  const arr = (v: unknown) => (Array.isArray(v) ? v : []);
  return {
    regions: arr(e?.regions),
    cities: arr(e?.cities),
    places: arr(e?.places),
    neighborhoods: arr(e?.neighborhoods),
    zips: arr(e?.zips),
    customLocations: arr(e?.customLocations),
  };
}
